'use client';

import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useFilteredVendorDiscovery } from '@/hooks/useFilteredVendorDiscovery';
import { useToast } from '@/hooks/use-toast';
import { analytics, ANALYTICS_EVENTS } from '@/lib/analytics';
import { 
  Search, 
  MapPin, 
  Star, 
  Building, 
  Users, 
  Filter,
  Clock,
  Shield,
  Award,
  Briefcase,
  ExternalLink,
  AlertTriangle
} from 'lucide-react';
import { Link } from 'react-router-dom';

interface SearchFilters {
  location?: string;
  
  certifications?: string[];
  capabilities?: string[];
  verification_level?: string;
  radius?: number;
  min_rating?: number;
  is_verified?: boolean;
}

export default function VendorSearchPage() {
  const { 
    vendors, 
    loading, 
    searchVendors, 
    getFeaturedVendors, 
    clearResults,
    businessRulesActive 
  } = useFilteredVendorDiscovery();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<SearchFilters>({});
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    // Load featured vendors on page load
    getFeaturedVendors();
  }, []);

  const handleSearch = async () => {
    if (!searchQuery.trim() && Object.keys(filters).length === 0) {
      getFeaturedVendors();
      return;
    }

    const searchFilters: SearchFilters = {
      ...filters,
      ...(searchQuery && { location: searchQuery })
    };

    await searchVendors(searchFilters);
    
    // Track search performed
    analytics.track({
      name: ANALYTICS_EVENTS.SEARCH_PERFORMED,
      category: 'engagement',
      properties: {
        search_type: 'vendor',
        search_query: searchQuery,
        has_filters: Object.keys(filters).length > 0,
      },
    });
  };

  const handleFilterChange = (key: keyof SearchFilters, value: any) => {
    // Don't set filter if "any" is selected (equivalent to no filter)
    if (value === 'any') {
      setFilters(prev => {
        const newFilters = { ...prev };
        delete newFilters[key];
        return newFilters;
      });
    } else {
      setFilters(prev => ({
        ...prev,
        [key]: value
      }));
    }
  };

  const clearAllFilters = () => {
    setFilters({});
    setSearchQuery('');
    clearResults();
    getFeaturedVendors();
  };

  const getVerificationBadge = (vendor: any) => {
    if (!vendor.is_verified) return null;
    
    const level = vendor.verification_level;
    const colors = {
      basic: 'bg-blue-500',
      enhanced: 'bg-green-500',
      premium: 'bg-purple-500'
    };
    
    return (
      <Badge 
        variant="secondary" 
        className={`${colors[level as keyof typeof colors] || 'bg-gray-500'} text-white`}
      >
        <Shield className="h-3 w-3 mr-1" />
        {level?.toUpperCase()}
      </Badge>
    );
  };

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold">Find Vendors</h1>
          <p className="text-muted-foreground">
            Browse vendors with documented capabilities for your next project
          </p>
        </div>

        {/* Business Rules Active Banner */}
        {businessRulesActive && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Your organization's vendor eligibility rules are active. Only vendors meeting your organization's minimum requirements are shown.
            </AlertDescription>
          </Alert>
        )}

        {/* Search Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" />
              Search Vendors
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Main Search */}
            <div className="flex gap-2">
              <div className="flex-1">
                <Input
                  placeholder="Search by location, business type, or keywords..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>
              <Button onClick={() => setShowFilters(!showFilters)} variant="outline">
                <Filter className="h-4 w-4 mr-2" />
                Filters
              </Button>
              <Button onClick={handleSearch} disabled={loading}>
                {loading ? 'Searching...' : 'Search'}
              </Button>
            </div>

            {/* Advanced Filters */}
            {showFilters && (
              <div className="border-t pt-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                  <div className="space-y-2">
                    <Label>Verification Level</Label>
                    <Select
                      value={filters.verification_level || 'any'}
                      onValueChange={(value) => handleFilterChange('verification_level', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Any verification" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any verification</SelectItem>
                        <SelectItem value="basic">Basic</SelectItem>
                        <SelectItem value="enhanced">Enhanced</SelectItem>
                        <SelectItem value="premium">Premium</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Minimum Rating</Label>
                    <Select
                      value={filters.min_rating?.toString() || 'any'}
                      onValueChange={(value) => handleFilterChange('min_rating', value === 'any' ? undefined : parseFloat(value))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Any rating" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any rating</SelectItem>
                        <SelectItem value="3">3+ Stars</SelectItem>
                        <SelectItem value="4">4+ Stars</SelectItem>
                        <SelectItem value="4.5">4.5+ Stars</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button onClick={handleSearch} disabled={loading}>
                    Apply Filters
                  </Button>
                  <Button onClick={clearAllFilters} variant="outline">
                    Clear All
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Results */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">
              {vendors.length > 0 ? `${vendors.length} Vendors Found` : 'Featured Vendors'}
            </h2>
            {vendors.length > 0 && (
              <Select defaultValue="relevance">
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="relevance">Sort by Relevance</SelectItem>
                  <SelectItem value="rating">Highest Rating</SelectItem>
                  <SelectItem value="experience">Most Experience</SelectItem>
                  <SelectItem value="recent">Recently Active</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-muted rounded-full"></div>
                      <div className="space-y-2 flex-1">
                        <div className="h-4 bg-muted rounded w-3/4"></div>
                        <div className="h-3 bg-muted rounded w-1/2"></div>
                        <div className="h-3 bg-muted rounded w-2/3"></div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : vendors.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {vendors.map((vendor) => (
                <Card key={vendor.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="space-y-4">
                      {/* Header */}
                      <div className="flex items-start gap-4">
                        <Avatar className="h-12 w-12">
                          <AvatarFallback>
                            <Building className="h-6 w-6" />
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold truncate">{vendor.organization_name || 'Unknown'}</h3>
                          
                          <div className="flex items-center gap-2 mt-1">
                            {vendor.country && (
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <MapPin className="h-3 w-3" />
                                {vendor.state && `${vendor.state}, `}
                                {vendor.country}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Verification & Rating */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {getVerificationBadge(vendor)}
                          {vendor.match_score && (
                            <Badge variant="outline">
                              {vendor.match_score}% Match
                            </Badge>
                          )}
                        </div>
                        {vendor.average_rating > 0 && (
                          <div className="flex items-center gap-1">
                            <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                            <span className="text-sm font-medium">{vendor.average_rating.toFixed(1)}</span>
                          </div>
                        )}
                      </div>

                      {/* Description */}
                      {vendor.business_description && (
                        <p className="text-sm text-muted-foreground line-clamp-3">
                          {vendor.business_description}
                        </p>
                      )}

                      {/* Stats */}
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div className="flex items-center gap-2">
                          <Briefcase className="h-4 w-4 text-muted-foreground" />
                          <span>{vendor.total_jobs_completed} Projects</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <span>{vendor.on_time_delivery_rate.toFixed(0)}% On-time</span>
                        </div>
                      </div>

                      {/* Service Categories */}
                      {vendor.service_categories && Array.isArray(vendor.service_categories) && vendor.service_categories.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {vendor.service_categories.slice(0, 3).map((category: any, index: number) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              {typeof category === 'string' ? category : category.category || 'Service'}
                            </Badge>
                          ))}
                          {vendor.service_categories.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{vendor.service_categories.length - 3} more
                            </Badge>
                          )}
                        </div>
                      )}

                      <Separator />

                      {/* Actions */}
                      <div className="flex gap-2">
                        <Button asChild className="flex-1">
                          <Link to={`/vendors/${vendor.id}`}>
                            View Profile
                            <ExternalLink className="h-4 w-4 ml-2" />
                          </Link>
                        </Button>
                        <Button variant="outline" size="sm">
                          Contact
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-12 text-center">
                <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No vendors found</h3>
                <p className="text-muted-foreground mb-4">
                  Try adjusting your search criteria or browse our featured vendors
                </p>
                <Button onClick={() => getFeaturedVendors()}>Browse Featured Vendors</Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}